import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import apiClient from '../api/client'
import type { JobState } from '../api/types'

interface RunResponse {
  id: string
  research_question_id: string
  collection_name: string
  status: JobState
  min_iterations: number
  max_iterations: number
  iterations_executed: number
  final_response: string | null
  final_citations: { doc_index: number; doc_id?: string | null; title?: string | null; reason?: string | null }[] | null
  current_step: string | null
  current_step_detail: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

interface IterationResponse {
  id: string
  research_run_id: string
  iteration_index: number
  status: string
  retrieved_doc_count: number | null
  proposed_queries: string[] | null
  executed_queries: string[] | null
  clustering_analysis: {
    global?: { n_documents: number; n_bertopic_topics: number; n_embedding_clusters: number; n_outliers: number }
    topics?: {
      topic_id: number
      topic_name: string
      n_docs: number
      compactness: number | null
      redundancy: number | null
      effective_information_score: number | null
      embedding_clusters_present?: number[]
      documents?: { doc_id: number; doi: string | null; openalex_id: string | null; preview: string; cluster_id: number; assigned_topic_prob: number | null }[]
      subclusters?: { subcluster_id: number; n_docs: number; doc_ids: number[]; keywords: string[] }[]
    }[]
  } | null
  sufficiency_assessment: { sufficient: boolean; missing_information?: string; stop_reason?: string } | null
  started_at: string
  completed_at: string | null
}

function StatusBadge({ state }: { state: JobState }): JSX.Element {
  return <span className={`badge badge--${state}`}>{state.replace(/_/g, ' ')}</span>
}

function formatStep(step: string | null, detail: Record<string, unknown> | null): string {
  if (!step) return 'Running...'
  const d = detail ?? {}
  switch (step) {
    case 'initializing':
      return `Initializing iteration ${(d.iteration as number) ?? ''}`.trim()
    case 'retrieving_kb':
      return `Retrieving relevant documents (iteration ${d.iteration as number})`
    case 'clustering_topics':
      return `Clustering topics — ${d.retrieved as number} docs retrieved`
    case 'assessing_sufficiency':
      return `Evaluating if data is sufficient...`
    case 'generating_queries':
      return `Generating search queries...`
    case 'waiting_for_user':
      return `Waiting for query review (${Array.isArray(d.proposed_queries) ? (d.proposed_queries as string[]).length : ''} queries)`.trim()
    case 'enriching_kb':
      return `Enriching knowledge base (${d.total_queries as number} queries)`
    case 'fetching_docs': {
      const q = d.query as string | undefined
      const idx = d.index as number | undefined
      const total = d.total as number | undefined
      return `Fetching documents for query "${q ?? ''}"${idx && total ? ` (${idx}/${total})` : ''}`
    }
    case 'writing_to_chroma': {
      const q = d.query as string | undefined
      const idx = d.index as number | undefined
      const total = d.total as number | undefined
      return `Writing to Chroma for query "${q ?? ''}"${idx && total ? ` (${idx}/${total})` : ''}`
    }
    case 'generating_answer':
      return `Generating final answer...`
    case 'completed':
      return `Completed`
    case 'failed':
      return `Failed${d.error ? `: ${d.error as string}` : ''}`
    default:
      return step.replace(/_/g, ' ')
  }
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const sec = Math.floor(diff / 1000)
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  return `${hr}h ago`
}

function RunDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [run, setRun] = useState<RunResponse | null>(null)
  const [iterations, setIterations] = useState<IterationResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState<{ doc_id: string | null; title: string | null; preview: string; page_content?: string; doi?: string | null; openalex_id?: string | null } | null>(null)

  const fetchData = async () => {
    if (!id) return
    setIsFetching(true)
    try {
      const runRes = await apiClient.get<RunResponse>(`/research/runs/${id}`)
      setRun(runRes.data as unknown as RunResponse)
      const itRes = await apiClient.get<IterationResponse[]>(`/research/runs/${id}/iterations`)
      setIterations(itRes.data as unknown as IterationResponse[])
      setError(null)
    } catch (err: unknown) {
      const maybeAxios = err as { response?: { data?: { detail?: string } } }
      setError(maybeAxios?.response?.data?.detail ?? 'Failed to load run')
    } finally {
      setLoading(false)
      setIsFetching(false)
    }
  }

  useEffect(() => {
    fetchData()
    const interval = setInterval(() => {
      if (run && ['completed', 'failed', 'cancelled'].includes(run.status)) return
      fetchData()
    }, 3000)
    return () => clearInterval(interval)
  }, [id, run?.status])

  const handleCancel = async () => {
    if (!id || !run) return
    if (!confirm('Cancel this run? Already-added documents will remain in the collection.')) return
    setCancelling(true)
    try {
      await apiClient.post(`/research/runs/${id}/cancel`)
      await fetchData()
    } catch (err: unknown) {
      const maybeAxios = err as { response?: { data?: { detail?: string } } }
      alert(maybeAxios?.response?.data?.detail ?? 'Cancel failed')
    } finally {
      setCancelling(false)
    }
  }

  const handleExport = async () => {
    if (!id) return
    setExporting(true)
    try {
      const res = await apiClient.get(`/research/runs/${id}/export`)
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `run-${id}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      alert('Export failed')
    } finally {
      setExporting(false)
    }
  }

  if (loading) return <div className="page-header"><h1 className="page-title">Loading run {id}...</h1></div>
  if (error) return <div><p style={{ color: '#c00' }}>{error}</p><Link to="/dashboard">Back to Dashboard</Link></div>
  if (!run) return <div>Run not found</div>

  const isTerminal = ['completed', 'failed', 'cancelled'].includes(run.status)

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Run {id?.slice(0, 8)}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <StatusBadge state={run.status} />
          <button className="page-action-secondary" onClick={fetchData} disabled={isFetching} title="Refresh">
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
          <Link to="/dashboard" className="page-action-secondary">Back to Dashboard</Link>
        </div>
      </div>

      {!['completed', 'failed', 'cancelled'].includes(run.status) && run.current_step && (
        <div style={{ background: run.status === 'waiting_for_user' ? '#fff8e1' : '#e3f2fd', border: `1px solid ${run.status === 'waiting_for_user' ? '#ffe082' : '#90caf9'}`, borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ marginRight: 8 }}>{run.status === 'running' ? '●' : '◐'}</span>
            <strong>{formatStep(run.current_step, run.current_step_detail)}</strong>
            <span style={{ fontSize: 12, color: '#555', marginLeft: 12 }}>updated {timeAgo(run.updated_at)}</span>
            {run.current_step_detail && Object.keys(run.current_step_detail).length > 0 && run.current_step !== 'waiting_for_user' && (
              <span style={{ fontSize: 12, color: '#666', marginLeft: 8 }}>
                — {JSON.stringify(run.current_step_detail).slice(0, 120)}
              </span>
            )}
          </div>
          <span style={{ fontSize: 12, color: '#1e88e5' }}>auto-refresh every 3s</span>
        </div>
      )}

      <div className="detail-grid">
        <div className="detail-card">
          <h2 className="detail-card-title">Overview</h2>
          <div className="detail-stats">
            <div className="stat"><span className="stat-value">{run.iterations_executed} / {run.max_iterations}</span><span className="stat-label">Iterations</span></div>
            <div className="stat"><span className="stat-value">{run.collection_name}</span><span className="stat-label">Collection</span></div>
            <div className="stat"><span className="stat-value">{iterations.reduce((acc, it) => acc + (it.retrieved_doc_count ?? 0), 0)}</span><span className="stat-label">Papers Retrieved</span></div>
          </div>
          <div style={{ fontSize: 13, color: '#666', marginTop: 8 }}>
            <div>Min: {run.min_iterations} · Created: {new Date(run.created_at).toLocaleString()}</div>
            <div>Updated: {new Date(run.updated_at).toLocaleString()} ({timeAgo(run.updated_at)})</div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {!isTerminal && <button className="button-danger" onClick={handleCancel} disabled={cancelling}>{cancelling ? 'Cancelling...' : 'Cancel Run'}</button>}
            <button className="page-action-secondary" onClick={handleExport} disabled={exporting}>{exporting ? 'Exporting...' : 'Export JSON'}</button>
            {run.status === 'waiting_for_user' && <button className="button-primary" onClick={() => navigate(`/runs/${id}/review`)}>Review queries</button>}
          </div>
        </div>

        <div className="detail-card detail-card-wide">
          <h2 className="detail-card-title">Topics by Iteration</h2>
          {iterations.length === 0 || iterations.every((it) => !it.clustering_analysis?.topics?.length) ? (
            <p style={{ fontSize: 14, color: '#666' }}>No topics yet — run is {run.status}.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {iterations.map((it) => {
                const topics = it.clustering_analysis?.topics ?? []
                const global = it.clustering_analysis?.global
                if (topics.length === 0) return null
                return (
                  <details key={it.id} open={it.iteration_index === iterations[iterations.length - 1].iteration_index} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                      Iteration {it.iteration_index} — {topics.length} topics, {global?.n_documents ?? it.retrieved_doc_count ?? 0} docs
                      {global && <span style={{ fontWeight: 400, color: '#666', marginLeft: 8 }}>({global.n_bertopic_topics} BERTopic, {global.n_embedding_clusters} clusters, {global.n_outliers} outliers)</span>}
                    </summary>
                    <table className="data-table" style={{ marginTop: 12 }}>
                      <thead>
                        <tr>
                          <th>Topic</th>
                          <th style={{ textAlign: 'right' }}>Docs</th>
                          <th style={{ textAlign: 'right' }}>Compactness</th>
                          <th style={{ textAlign: 'right' }}>Redundancy</th>
                          <th style={{ textAlign: 'right' }}>Effective</th>
                          <th>Clusters</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topics.map((t) => (
                          <tr key={`${it.iteration_index}-${t.topic_id}`} style={{ cursor: 'pointer' }} onClick={() => navigate(`/runs/${id}/topics/${it.iteration_index}/${t.topic_id}`)}>
                            <td style={{ maxWidth: 260 }}>
                              <Link to={`/runs/${id}/topics/${it.iteration_index}/${t.topic_id}`} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 500, color: 'var(--primary)', textDecoration: 'none' }}>
                                {t.topic_id === -1 ? 'Outliers' : t.topic_name}
                              </Link>
                              <span style={{ fontSize: 12, color: '#999', marginLeft: 6 }}>#{t.topic_id}</span>
                            </td>
                            <td style={{ textAlign: 'right' }}>{t.n_docs}</td>
                            <td style={{ textAlign: 'right' }}>{t.compactness != null ? t.compactness.toFixed(2) : '-'}</td>
                            <td style={{ textAlign: 'right' }}>{t.redundancy != null ? t.redundancy.toFixed(2) : '-'}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{t.effective_information_score != null ? t.effective_information_score.toFixed(2) : '-'}</td>
                            <td style={{ fontSize: 12, color: '#666' }}>{t.embedding_clusters_present?.join(', ') ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )
              })}
            </div>
          )}
        </div>

        <div className="detail-card detail-card-wide">
          <h2 className="detail-card-title">Iterations</h2>
          {iterations.length === 0 ? (
            <p style={{ fontSize: 14, color: '#666' }}>No iterations yet.</p>
          ) : (
            <table className="data-table">
              <thead><tr><th>#</th><th>Retrieved</th><th>Proposed Queries</th><th>Executed Queries</th><th>Sufficiency</th></tr></thead>
              <tbody>
                {iterations.map((it) => (
                  <tr key={it.id}>
                    <td>{it.iteration_index}</td>
                    <td>{it.retrieved_doc_count ?? '-'}</td>
                    <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.proposed_queries?.join(' | ') ?? '-'}</td>
                    <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.executed_queries?.join(' | ') ?? '-'}</td>
                    <td>{it.sufficiency_assessment ? (it.sufficiency_assessment.sufficient ? 'Sufficient' : 'Insufficient') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="detail-card detail-card-wide">
          <h2 className="detail-card-title">Sufficiency per Iteration</h2>
          {iterations.length === 0 ? (
            <span style={{ fontSize: 14, color: '#666' }}>—</span>
          ) : (
            <table className="data-table" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ width: 100 }}>Iteration</th>
                  <th style={{ width: 130 }}>Assessment</th>
                  <th>Missing Information / Stop Reason</th>
                </tr>
              </thead>
              <tbody>
                {iterations.map((it) => (
                  <tr key={it.id} className={it.sufficiency_assessment?.sufficient ? 'row-sufficient' : 'row-insufficient'}>
                    <td>Iteration {it.iteration_index}</td>
                    <td>
                      <span className={`badge ${it.sufficiency_assessment?.sufficient ? 'badge--completed' : 'badge--failed'}`}>
                        {it.sufficiency_assessment?.sufficient ? 'Sufficient' : 'Insufficient'}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: 13, color: '#475569' }}>
                      {it.sufficiency_assessment?.missing_information || it.sufficiency_assessment?.stop_reason || <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {run.final_response && (
          <div className="detail-card detail-card-wide">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h2 className="detail-card-title" style={{ margin: 0 }}>Final Response</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <Link to={`/runs/${id}/documents`} className="page-action-secondary" style={{ fontSize: 12 }}>Documents →</Link>
                <Link to={`/runs/${id}/analysis`} className="page-action-secondary" style={{ fontSize: 12 }}>Technical Analysis →</Link>
              </div>
            </div>
            <div className="final-response" style={{ whiteSpace: 'pre-wrap' }}>
              {(() => {
                const text = run.final_response!
                const citations = run.final_citations ?? []
                const allDocs = iterations.flatMap((it) => it.clustering_analysis?.topics?.flatMap((t) => t.documents ?? []) ?? [])
                const findDoc = (idx: number) => {
                  const cite = citations.find((c) => c.doc_index === idx)
                  if (cite?.doc_id) {
                    const byId = allDocs.find((d) => d.doi === cite.doc_id || d.openalex_id === cite.doc_id)
                    if (byId) return byId
                  }
                  return allDocs[idx - 1] ?? null
                }
                const parts = text.split(/(\[Doc\s*\d+\])/g)
                return parts.map((part, i) => {
                  const m = part.match(/\[Doc\s*(\d+)\]/)
                  if (m) {
                    const idx = Number(m[1])
                    const doc = findDoc(idx)
                    return (
                      <a
                        key={i}
                        onClick={() => doc && setSelectedDoc({ doc_id: doc.doc_id?.toString() ?? String(idx), title: (doc as unknown as { preview?: string }).preview ?? null, preview: (doc as unknown as { preview?: string }).preview ?? '', page_content: (doc as unknown as { page_content?: string }).page_content, doi: doc.doi, openalex_id: doc.openalex_id })}
                        style={{ color: 'var(--primary)', cursor: doc ? 'pointer' : 'default', fontWeight: 600, textDecoration: 'underline' }}
                        title={doc ? `Doc ${idx}: ${(doc as unknown as { preview?: string }).preview?.slice(0, 80)}` : `Doc ${idx}`}
                      >
                        {part}
                      </a>
                    )
                  }
                  return <span key={i}>{part}</span>
                })
              })()}
            </div>
            {run.final_citations && run.final_citations.length > 0 && (
              <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>References</h3>
                <ul style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {run.final_citations.map((c, i) => (
                    <li key={i} style={{ display: 'flex', gap: 8 }}>
                      <span style={{ color: '#64748b' }}>[Doc {c.doc_index}]</span>
                      <span
                        onClick={() => {
                          const allDocs = iterations.flatMap((it) => it.clustering_analysis?.topics?.flatMap((t) => t.documents ?? []) ?? [])
                          const doc = allDocs[(c.doc_index as number) - 1] ?? allDocs.find((d) => d.doi === c.doc_id || d.openalex_id === c.doc_id)
                          if (doc) setSelectedDoc({ doc_id: doc.doc_id?.toString() ?? String(c.doc_index), title: (doc as unknown as { preview?: string }).preview ?? c.title ?? null, preview: (doc as unknown as { preview?: string }).preview ?? '', page_content: (doc as unknown as { page_content?: string }).page_content, doi: doc.doi, openalex_id: doc.openalex_id })
                        }}
                        style={{ color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        {c.title ?? c.doc_id ?? `Doc ${c.doc_index}`}
                      </span>
                      {c.reason && <span style={{ color: '#94a3b8' }}>— {c.reason.slice(0, 80)}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {selectedDoc && (
          <div onClick={() => setSelectedDoc(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, maxWidth: 720, width: '100%', maxHeight: '80vh', overflow: 'auto', padding: 24, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ fontSize: 16, fontWeight: 700 }}>Document {selectedDoc.doc_id}</h2>
                <button onClick={() => setSelectedDoc(null)} style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>Close</button>
              </div>
              <div style={{ fontSize: 13, color: '#475569', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selectedDoc.doi && <div><strong>DOI:</strong> <a href={selectedDoc.doi.startsWith('http') ? selectedDoc.doi : `https://doi.org/${selectedDoc.doi}`} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>{selectedDoc.doi}</a></div>}
                {selectedDoc.openalex_id && <div><strong>OpenAlex:</strong> <a href={selectedDoc.openalex_id} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>{selectedDoc.openalex_id}</a></div>}
                {selectedDoc.title && <div><strong>Title:</strong> {selectedDoc.title}</div>}
                <div style={{ marginTop: 8 }}><strong>Document:</strong><p style={{ whiteSpace: 'pre-wrap', marginTop: 4, background: '#f8fafc', padding: 12, borderRadius: 8, maxHeight: 400, overflow: 'auto' }}>{selectedDoc.page_content ?? selectedDoc.preview}</p></div>
              </div>
            </div>
          </div>
        )}

        {run.status === 'waiting_for_user' && (
          <div className="detail-card detail-card-wide" style={{ background: '#fff8e1', border: '1px solid #ffe082' }}>
            <h2 className="detail-card-title">Action required</h2>
            <p>This run is waiting for query review.</p>
            <Link to={`/runs/${id}/review`} className="button-primary">Go to Review</Link>
          </div>
        )}
      </div>
    </div>
  )
}

export default RunDetailPage
